/**
 * Mock Database
 *
 * Centralized localStorage-based database for development.
 * Provides CRUD operations that can be easily swapped for real API calls later.
 */

import type { UUID, BaseEntity } from '../types'

// Storage configuration
const STORAGE_PREFIX = 'docurious-'
const STORAGE_VERSION = '1'

/**
 * Generate a UUID v4
 */
export function generateId(): UUID {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get current ISO timestamp
 */
export function timestamp(): string {
  return new Date().toISOString()
}

/**
 * Simulate network delay
 */
export function delay(ms: number = 100): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Collection names
 */
export type CollectionName =
  | 'users'
  | 'sessions'
  | 'challenges'
  | 'categories'
  | 'userChallenges'
  | 'trackRecords'
  | 'trackRecordEntries'
  | 'trackRecordMedia'
  | 'communities'
  | 'communityMemberships'
  | 'communityFeed'
  | 'feedComments'
  | 'communityAssignments'
  | 'studentAssignmentProgress'
  | 'gifts'
  | 'giftPermissions'
  | 'challengeInvitations'
  | 'invitationRecipients'
  | 'invitationPermissions'
  | 'pendingInvitations'
  | 'schools'
  | 'classes'
  | 'studentRoster'
  | 'teacherRoster'
  | 'classAssignments'
  | 'assignmentProgress'
  | 'notifications'
  | 'notificationPreferences'
  | 'savedList'
  | 'curatedViews'
  | 'badges'
  | 'userBadges'
  | 'xpEvents'
  | 'deletionRequests'
  | 'dataExports'
  | 'gradeLevels'
  | 'trackRecordComments'
  | 'rejectionReasonTemplates'
  | 'events'
  | 'eventRegistrations'
  | 'learningPaths'
  | 'portfolios'
  | 'reflectionPrompts'
  | 'reports'
  | 'communityGoals'
  | 'goalContributions'
  | 'goalParticipations'
  | 'paymentMethods'
  | 'checkoutSessions'
  | 'transactions'
  | 'subscriptions'
  | 'parentVerifications'
  | 'accountLinkages'
  | 'reflectionResponses'
  | 'challengeRequests'
  | 'surveys'
  | 'surveyCampaigns'
  | 'onboardingProgress'
  | 'sharedItems'
  | 'auditLogs'
  | 'flaggedContent'
  | 'adminRoles'
  | 'moderationActions'
  | 'dealersChoiceGames'
  | 'dcHistory'
  | 'questionBankPrompts'
  | 'featureEducation'
  | 'purchaseRequests'
  | 'challengeInstances'
  | 'walletTransactions'
  | 'payouts'
  | 'seriesOrders'
  | 'vendors'
  | 'providerFeedback'
  | 'entryReactions'

/**
 * Get storage key for a collection
 */
function getStorageKey(collection: CollectionName): string {
  return `${STORAGE_PREFIX}v${STORAGE_VERSION}-${collection}`
}

/**
 * Mock Database class
 */
class MockDatabase {
  private cache: Map<CollectionName, Map<UUID, unknown>> = new Map()

  constructor() {
    this.loadFromStorage()
  }

  /**
   * Load all collections from localStorage
   */
  private loadFromStorage(): void {
    const collections: CollectionName[] = [
      'users',
      'sessions',
      'challenges',
      'categories',
      'userChallenges',
      'trackRecords',
      'trackRecordEntries',
      'trackRecordMedia',
      'communities',
      'communityMemberships',
      'communityFeed',
      'feedComments',
      'communityAssignments',
      'studentAssignmentProgress',
      'gifts',
      'giftPermissions',
      'challengeInvitations',
      'invitationRecipients',
      'invitationPermissions',
      'pendingInvitations',
      'schools',
      'classes',
      'studentRoster',
      'teacherRoster',
      'classAssignments',
      'assignmentProgress',
      'notifications',
      'notificationPreferences',
      'savedList',
      'curatedViews',
      'badges',
      'userBadges',
      'xpEvents',
      'deletionRequests',
      'dataExports',
      'gradeLevels',
      'trackRecordComments',
      'rejectionReasonTemplates',
      'events',
      'eventRegistrations',
      'learningPaths',
      'portfolios',
      'reflectionPrompts',
      'reports',
      'communityGoals',
      'goalContributions',
      'goalParticipations',
      'paymentMethods',
      'checkoutSessions',
      'transactions',
      'subscriptions',
      'parentVerifications',
      'accountLinkages',
      'reflectionResponses',
      'challengeRequests',
      'surveys',
      'surveyCampaigns',
      'onboardingProgress',
      'sharedItems',
      'auditLogs',
      'flaggedContent',
      'adminRoles',
      'moderationActions',
      'dealersChoiceGames',
      'dcHistory',
      'questionBankPrompts',
      'featureEducation',
      'purchaseRequests',
      'vendors',
      'providerFeedback',
      'entryReactions',
    ]

    for (const name of collections) {
      const key = getStorageKey(name)
      const stored = localStorage.getItem(key)
      const map = new Map<UUID, unknown>()

      if (stored) {
        try {
          const data = JSON.parse(stored) as Record<UUID, unknown>
          for (const [id, item] of Object.entries(data)) {
            map.set(id, item)
          }
        } catch (e) {
          console.warn(`Failed to parse ${name} from storage`, e)
        }
      }

      this.cache.set(name, map)
    }
  }

  /**
   * Save a collection to localStorage
   */
  private saveToStorage(collection: CollectionName): void {
    const map = this.cache.get(collection)
    if (!map) return

    const data: Record<UUID, unknown> = {}
    for (const [id, item] of map.entries()) {
      data[id] = item
    }

    localStorage.setItem(getStorageKey(collection), JSON.stringify(data))
  }

  /**
   * Get all items in a collection
   */
  getAll<T>(collection: CollectionName): T[] {
    const map = this.cache.get(collection)
    if (!map) return []
    return Array.from(map.values()) as T[]
  }

  /**
   * Find item by ID
   */
  findById<T>(collection: CollectionName, id: UUID): T | null {
    const map = this.cache.get(collection)
    if (!map) return null
    return (map.get(id) as T) ?? null
  }

  /**
   * Find items matching a predicate
   */
  findWhere<T>(collection: CollectionName, predicate: (item: T) => boolean): T[] {
    const items = this.getAll<T>(collection)
    return items.filter(predicate)
  }

  /**
   * Find first item matching a predicate
   */
  findOne<T>(collection: CollectionName, predicate: (item: T) => boolean): T | null {
    const items = this.getAll<T>(collection)
    return items.find(predicate) ?? null
  }

  /**
   * Insert a new item
   */
  insert<T extends BaseEntity>(
    collection: CollectionName,
    data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>
  ): T {
    const map = this.cache.get(collection)
    if (!map) {
      throw new Error(`Collection ${collection} not found`)
    }

    const now = timestamp()
    const item = {
      ...data,
      id: generateId(),
      createdAt: now,
      updatedAt: now,
    } as T

    map.set(item.id, item)
    this.saveToStorage(collection)

    return item
  }

  /**
   * Insert with specific ID (for seeding)
   */
  insertWithId<T extends BaseEntity>(collection: CollectionName, data: T): T {
    const map = this.cache.get(collection)
    if (!map) {
      throw new Error(`Collection ${collection} not found`)
    }

    const now = timestamp()
    const item = {
      ...data,
      createdAt: data.createdAt ?? now,
      updatedAt: data.updatedAt ?? now,
    } as T

    map.set(item.id, item)
    this.saveToStorage(collection)

    return item
  }

  /**
   * Update an existing item
   */
  update<T extends BaseEntity>(
    collection: CollectionName,
    id: UUID,
    data: Partial<T>
  ): T | null {
    const map = this.cache.get(collection)
    if (!map) return null

    const existing = map.get(id) as T | undefined
    if (!existing) return null

    // Strip undefined values so we don't overwrite existing fields
    const cleanData = Object.fromEntries(
      Object.entries(data as Record<string, unknown>).filter(([, v]) => v !== undefined)
    )

    const updated = {
      ...existing,
      ...cleanData,
      id, // Prevent ID from being changed
      updatedAt: timestamp(),
    } as T

    map.set(id, updated)
    this.saveToStorage(collection)

    return updated
  }

  /**
   * Delete an item
   */
  delete(collection: CollectionName, id: UUID): boolean {
    const map = this.cache.get(collection)
    if (!map) return false

    const deleted = map.delete(id)
    if (deleted) {
      this.saveToStorage(collection)
    }
    return deleted
  }

  /**
   * Clear a collection
   */
  clear(collection: CollectionName): void {
    const map = this.cache.get(collection)
    if (map) {
      map.clear()
      this.saveToStorage(collection)
    }
  }

  /**
   * Clear all collections
   */
  clearAll(): void {
    for (const [name, map] of this.cache.entries()) {
      map.clear()
      this.saveToStorage(name)
    }
  }

  /**
   * Count items in a collection
   */
  count(collection: CollectionName, predicate?: (item: unknown) => boolean): number {
    if (predicate) {
      return this.findWhere(collection, predicate).length
    }
    const map = this.cache.get(collection)
    return map?.size ?? 0
  }

  /**
   * Check if seeded
   */
  isSeeded(): boolean {
    return localStorage.getItem(`${STORAGE_PREFIX}seeded`) === 'true'
  }

  /**
   * Mark as seeded
   */
  markSeeded(): void {
    localStorage.setItem(`${STORAGE_PREFIX}seeded`, 'true')
  }
}

// Export singleton instance
export const mockDb = new MockDatabase()

// Export helper functions
export { getStorageKey }
